import { mat4 } from "gl-matrix";
class Step {
  constructor(draw, length) {
    this.draw = draw;
    this.length = length;
  }

  after(rhs) {
    const r = mat4.create();
    return new Step(
      mat4.multiply(r, rhs.draw, this.draw),
      this.length + rhs.length
    );
  }
}
Step.move = (x, length) => {
  if (length === undefined) length = x;
  return new Step(
    mat4.fromValues(1, 0, x, 0, 0, 1, 0, x, 0, 0, 1, 0, 0, 0, 0, 1),
    length
  );
};
Step.rotate = (a, length) => {
  if (length === undefined) length = 0;
  const c = Math.cos(a),
    s = Math.sin(a);
  return new Step(
    mat4.fromValues(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, c, s, 0, 0, -s, c),
    length
  );
};
Step.I = new Step(mat4.create(), 0);

class Calculator {
  constructor(rules, maxDepth, axiom, scaleBase) {
    this.maxDepth = maxDepth;
    this.rules = rules;
    this.cache = [];
    this.axiom = axiom;
    this.scaleBase = scaleBase || 1;
    for (let i = 0; i < maxDepth; ++i) this.cache.push({});
  }
  getAction(start, depth) {
    if (start.length > 1) {
      let a = this.getAction(start[0], depth);
      for (let i = 1; i < start.length; ++i) {
        a = this.getAction(start[i], depth).after(a);
      }
      return a;
    } else if (start.length === 0) return Step.I;

    if (depth === 0) return this.rules[start].draw;

    if (this.cache[depth][start]) {
      return this.cache[depth][start];
    }

    return (this.cache[depth][start] = this.getAction(
      this.rules[start].rule,
      depth - 1
    ));
  }
  getShader(axiom) {
    if (!axiom) axiom = this.axiom;
    let s = "";
    const keys = Object.keys(this.rules);
    const max_rule_length = Math.max(
      ...keys.map(k => this.rules[k].rule.length)
    );

    const print_rule = (rule, r, depth) => {
      let s = "\n";
      for (let i = 0; i < max_rule_length; ++i) {
        s += `${r}[${i}].c = `;
        s += (i < rule.length ? keys.indexOf(rule[i]) : -1).toFixed(0);
        s += ";\n";
      }
      for (let j = 0; j < this.maxDepth; ++j) {
        if (j > 0) s += "else ";
        s += `if(${depth} == ${j}){\n`;
        for (let i = 0; i < rule.length; ++i) {
          const action = this.getAction(rule[i], j);
          s += `${r}[${i}].length = `;
          s += action.length.toFixed(7);
          s += ";\n";
          s += `${r}[${i}].action = `;
          s += mat4.str(action.draw);
          s += ";\n";
        }
        s += "}\n";
      }

      return s;
    };

    const getRule = (c, depth, r) => {
      return `if(${c} == -1) {
        ${print_rule(axiom, r, depth)}
      } else ${keys
        .map((k, i) => {
          return `if(${c} == ${i}) {${print_rule(
            this.rules[k].rule,
            r,
            depth
          )}}`;
        })
        .join(" else ")}`;
    };

    s += `
    uniform float width;
    uniform float height;

    struct rule {
      int c;
      float length;
      mat4 action;
    };

    vec2 onCurve(float t) {
      rule r[max_rule_length];

      int c = -1;
      vec4 x0 = vec4(0.,0.,1.,0.);
      for(int depth = max_depth-1; depth >= 0; --depth) {
        ${getRule("c", "depth", "r")}
        float sum = 0.0;
        for(int i = 0; i < max_rule_length; ++i) {
          if(r[i].c != -1)
            sum += r[i].length;
        }
        t *= sum;

        int found = -1;
        for(int i = 0; i < max_rule_length; ++i) {
          if(found == -1) {
            if(t - r[i].length < 0.0 || r[i].c == -1) {
              if(r[i].c != -1) {
                c = r[i].c;
                t /= r[i].length;
                if(depth == 0) {
                  x0 = x0*mat4(
                    1.,0.,0.,0.,
                    0.,1.,0.,0.,
                    0.,0.,t, 0.,
                    0.,0.,0.,t)*r[i].action;
                }
              }
              found = i;
            } else {
              x0 = x0*r[i].action;
              t -= r[i].length;
            }
          }
        }
      }

      return x0.xy*pow(scale_base, float(max_depth-1));
    }

    void main() {
      float t = (gl_FragCoord.x + floor(gl_FragCoord.y)*width)/(width*height);
      gl_FragColor = vec4(onCurve(t), 0., 0.);
    }
    `;
    s = s
      .replace(/scale_base/g, this.scaleBase.toFixed(7))
      .replace(/axiom_length/g, axiom.length)
      .replace(/rule_count/g, keys.length)
      .replace(/max_depth/g, this.maxDepth)
      .replace(/max_rule_length/g, max_rule_length);
    return s;
  }
}

Calculator.Sierpinski = maxDepth =>
  new Calculator(
    {
      A: {
        rule: "B-A-B",
        draw: Step.move(1, 1)
      },
      B: {
        rule: "A+B+A",
        draw: Step.move(1, 1)
      },
      "+": {
        rule: "+",
        draw: Step.rotate(-Math.PI / 3, 0)
      },
      "-": {
        rule: "-",
        draw: Step.rotate(Math.PI / 3, 0)
      }
    },
    maxDepth,
    "A",
    0.5
  );

Calculator.Binary = maxDepth =>
  new Calculator(
    {
      Z: {
        rule: "OlZzrrZ",
        draw: Step.move(1, 1)
      },
      z: {
        rule: "zlo",
        draw: Step.move(-1, 0)
      },
      O: {
        rule: "OO",
        draw: Step.move(1, 1)
      },
      o: {
        rule: "oo",
        draw: Step.move(-1, 0)
      },
      l: {
        rule: "l",
        draw: Step.rotate(-Math.PI / 4, 0)
      },
      r: {
        rule: "r",
        draw: Step.rotate(Math.PI / 4, 0)
      }
    },
    maxDepth,
    "llZ",
    0.5
  );

Calculator.Fern = maxDepth =>
  new Calculator(
    {
      X: {
        rule: "F+Xx-XxF-FXxf++X",
        draw: Step.I
      },
      x: {
        rule: "x-ff",
        draw: Step.I
      },
      F: {
        rule: "FF",
        draw: Step.move(1, 1)
      },
      f: {
        rule: "ff",
        draw: Step.move(-1, 0)
      },
      "+": {
        rule: "+",
        draw: Step.rotate((-25 * Math.PI) / 180, 0)
      },
      "-": {
        rule: "-",
        draw: Step.rotate((25 * Math.PI) / 180, 0)
      }
    },
    maxDepth,
    "+++X",
    0.5
  );

Calculator.Dragon = maxDepth =>
  new Calculator(
    {
      X: {
        rule: "X+YF+",
        draw: Step.I
      },
      Y: {
        rule: "-FX-Y",
        draw: Step.I
      },
      F: {
        rule: "F",
        draw: Step.move(1, 1)
      },
      "+": {
        rule: "+",
        draw: Step.rotate((-90 * Math.PI) / 180, 0)
      },
      "-": {
        rule: "-",
        draw: Step.rotate((90 * Math.PI) / 180, 0)
      }
    },
    maxDepth,
    "FX",
    1 / Math.sqrt(2)
  );

Calculator.Koch = maxDepth =>
  new Calculator(
    {
      F: {
        rule: "F+F-F-FF+F+F-F",
        draw: Step.move(1, 1)
      },
      "+": {
        rule: "+",
        draw: Step.rotate((90 * Math.PI) / 180, 0)
      },
      "-": {
        rule: "-",
        draw: Step.rotate((-90 * Math.PI) / 180, 0)
      }
    },
    maxDepth,
    "F-F-F-F",
    1 / 4
  );

Calculator.Hilbert = maxDepth =>
  new Calculator(
    {
      F: {
        rule: "F",
        draw: Step.move(1, 1)
      },
      X: {
        rule: "-YF+XFX+FY-",
        draw: Step.I
      },
      Y: {
        rule: "+XF-YFY-FX+",
        draw: Step.I
      },
      "+": {
        rule: "+",
        draw: Step.rotate((90 * Math.PI) / 180, 0)
      },
      "-": {
        rule: "-",
        draw: Step.rotate((-90 * Math.PI) / 180, 0)
      }
    },
    maxDepth,
    "X",
    1 / 2
  );

Calculator.Tree = maxDepth =>
  new Calculator(
    {
      F: {
        rule: "FF--F+F+Ff-f-f+++F-F-Ff+f+f-",
        draw: Step.move(1, 1)
      },
      f: {
        rule: "ff",
        draw: Step.move(-1, 0)
      },
      "+": {
        rule: "+",
        draw: Step.rotate((22 * Math.PI) / 180, 0)
      },
      "-": {
        rule: "-",
        draw: Step.rotate((-22 * Math.PI) / 180, 0)
      }
    },
    maxDepth,
    "----F",
    1 / 2
  );

export default Calculator;
